/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020-2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { NgModule, APP_INITIALIZER } from '@angular/core';

import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';

// All of our common AngularJS providers needed in Angular:
import {
  BulbAjaxServiceProvider,
  BulbAssetLibraryOneDriveServiceProvider,
  BulbAssetLibraryServiceProvider,
  BulbAssetLibraryUnsplashServiceProvider,
  BulbAssetLibraryUploadServiceProvider,
  BulbAuthServiceProvider,
  BulbBrowserServiceProvider,
  BulbBusyCoverServiceProvider,
  BulbGroupServiceProvider,
  BulbPageServiceProvider,
  BulbUnitServiceProvider,
  StateProvider,
} from '../app/angularjs.providers';

// Common Services:
import { AppConfigService } from './services/app-config/app-config.service';
import { AnalyticsService } from './analytics.service';
import { BulbFetchUserService } from './fetch-user.service';
import { BulbUtilitiesService } from './services/bulb-utilities.service';
import { ContentService } from './content.service';
import { ErrorService } from './error.service';
import { EllipsifyDirective } from './ellipsify.directive';
import { FeatureFlagDirective } from './feature-flag.directive';
import { FeatureFlagService } from './feature-flag.service';
import { TranslationService } from './translation.service';
import { UploadAssetService } from './upload-asset.service';

// Common Pipes:
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { TranslatePipe } from './translate.pipe';
import { ComponentInjectorService } from './component-injector.service';
import { OnedriveAssetLibraryService } from './onedrive-assetlibrary.service';

// Custom Directives:
import { EmailUnique } from './validators/email-unique.directive';
import { ValidatePatternDirective } from './validators/validate-pattern.directive';

// Custom Components
import { ErrorComponent } from './errors/error.component';

/**
 * Main common module.
 */
@NgModule({
  providers: [
    // Angular providers:
    AnalyticsService,
    BulbFetchUserService,
    BulbUtilitiesService,
    ComponentInjectorService,
    ContentService,
    ErrorService,
    FeatureFlagService,
    OnedriveAssetLibraryService,
    TranslationService,
    UploadAssetService,
    // AngularJS providers:
    BulbAjaxServiceProvider,
    BulbAssetLibraryOneDriveServiceProvider,
    BulbAssetLibraryServiceProvider,
    BulbAssetLibraryUnsplashServiceProvider,
    BulbAssetLibraryUploadServiceProvider,
    BulbAuthServiceProvider,
    BulbBrowserServiceProvider,
    BulbBusyCoverServiceProvider,
    BulbGroupServiceProvider,
    BulbPageServiceProvider,
    BulbUnitServiceProvider,
    StateProvider,
    {
      provide: APP_INITIALIZER,
      useFactory: (appConfigService: AppConfigService) => () => appConfigService.loadAppConfig(),
      deps: [AppConfigService],
      multi: true,
    },
  ],
  declarations: [
    EmailUnique,
    ErrorComponent,
    FeatureFlagDirective,
    PhoneNumberPipe,
    TranslatePipe,
    ValidatePatternDirective,
    EllipsifyDirective,
  ],
  exports: [
    EmailUnique,
    FeatureFlagDirective,
    PhoneNumberPipe,
    TranslatePipe,
    ValidatePatternDirective,
    EllipsifyDirective,
  ],
  imports: [
    CommonModule,
    TranslocoModule,
  ],
})
export class BulbCommonModule {}
